import type { FC, ReactNode, Ref, RefAttributes } from 'react';
import type { SelectProps as AriaSelectProps } from 'react-aria-components';

import { t } from '@lingui/core/macro';
import { ChevronDown } from '@untitledui/icons/ChevronDown';
import { XClose } from '@untitledui/icons/XClose';
import { createContext, isValidElement, useContext } from 'react';
import {
  Button as AriaButton,
  ButtonContext,
  ListBox as AriaListBox,
  Select as AriaSelect,
  SelectStateContext,
  SelectValue as AriaSelectValue,
} from 'react-aria-components';

import { Avatar } from '@/components/base/avatar/avatar';
import { HintText } from '@/components/base/input/hint-text';
import { Label } from '@/components/base/input/label';
import { cx } from '@/utils/cx';
import { isReactComponent } from '@/utils/is-react-component';

import { ComboBox } from './combobox';
import { Popover } from './popover';
import { SelectItem } from './select-item';

export type SelectItemType<T extends string = string> = {
  id: T;
  label?: string;
  avatarUrl?: string;
  isDisabled?: boolean;
  supportingText?: string;
  icon?: FC | ReactNode;
};

export interface CommonProps {
  hint?: string;
  label?: string;
  tooltip?: string;
  size?: 'xs' | 'sm' | 'md';
  placeholder?: string;
}

interface SelectProps<T extends string = string>
  extends
    Omit<
      AriaSelectProps<SelectItemType<T>>,
      'children' | 'items' | 'onSelectionChange'
    >,
    RefAttributes<HTMLDivElement>,
    CommonProps {
  items?: SelectItemType<T>[];
  onSelectionChange?: (key: T) => void;
  popoverClassName?: string;
  placeholderIcon?: FC | ReactNode;
  /** Icon shown as compact trigger on mobile. Full select shown on desktop. */
  triggerIcon?: FC<{ className?: string }>;
  /** Icon rendered at the trailing end of the trigger (replaces the chevron). */
  trailingIcon?: ReactNode;
  /** Whether the user can clear the current selection via an inline X button next to the chevron. */
  isClearable?: boolean;
  onClear?: () => void;
  children: ReactNode | ((item: SelectItemType<T>) => ReactNode);
}

interface SelectValueProps {
  isOpen: boolean;
  size: 'xs' | 'sm' | 'md';
  isFocused: boolean;
  isDisabled: boolean;
  isInvalid?: boolean;
  placeholder?: string;
  ref?: Ref<HTMLButtonElement>;
  placeholderIcon?: FC | ReactNode;
  triggerIcon?: FC<{ className?: string }>;
  trailingIcon?: ReactNode;
  isClearable?: boolean;
  onClear?: () => void;
}

const emptyButtonContext = {};

export const sizes = {
  xs: { root: 'py-1.5 px-2.5', shortcut: 'pr-2' },
  sm: { root: 'py-2 px-3', shortcut: 'pr-2.5' },
  md: { root: 'py-2.5 px-3.5', shortcut: 'pr-3' },
};

const chevronSizeClass = (size: 'xs' | 'sm' | 'md') =>
  size === 'xs'
    ? 'size-3.5 stroke-[2.5px]'
    : size === 'sm'
      ? 'size-4 stroke-[2.5px]'
      : 'size-5';

const SelectValue = ({
  isOpen,
  isFocused,
  isDisabled,
  isInvalid = false,
  size,
  placeholder,
  placeholderIcon,
  triggerIcon: TriggerIcon,
  trailingIcon,
  isClearable = false,
  onClear,
  ref,
}: SelectValueProps) => {
  const selectState = useContext(SelectStateContext);
  const hasSelection = selectState?.selectedKey != null;
  const showClearButton = isClearable && hasSelection && !isDisabled;

  return (
    <div
      className={cx('relative', TriggerIcon ? 'w-auto sm:w-full' : 'w-full')}
    >
      <AriaButton
        ref={ref}
        className={cx(
          'relative flex w-full cursor-pointer items-center rounded-lg bg-primary shadow-xs ring-1 outline-hidden transition duration-100 ease-linear ring-inset',
          isInvalid ? 'ring-error_subtle' : 'ring-primary',
          (isFocused || isOpen) &&
            (isInvalid ? 'ring-2 ring-error' : 'ring-2 ring-brand'),
          isDisabled && 'cursor-not-allowed bg-disabled_subtle text-disabled',
        )}
      >
        {TriggerIcon && (
          <div className={cx(sizes[size].root, 'sm:hidden')}>
            <TriggerIcon className="size-5 text-fg-quaternary" />
          </div>
        )}
        <AriaSelectValue<SelectItemType>
          className={cx(
            'h-max w-full items-center justify-start gap-2 truncate text-left align-middle',

            // Icon styles
            '*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary in-disabled:*:data-icon:text-fg-disabled',

            TriggerIcon ? 'hidden sm:flex' : 'flex',
            sizes[size].root,
            trailingIcon && (size === 'sm' ? 'pr-9' : 'pr-10'),
          )}
        >
          {(state) => {
            const Icon = state.selectedItem?.icon || placeholderIcon;
            return (
              <>
                {state.selectedItem?.avatarUrl ? (
                  <Avatar
                    size="xs"
                    src={state.selectedItem.avatarUrl}
                    alt={state.selectedItem.label}
                  />
                ) : isReactComponent(Icon) ? (
                  <Icon data-icon aria-hidden="true" />
                ) : isValidElement(Icon) ? (
                  Icon
                ) : null}

                {state.selectedItem ? (
                  <section className="flex w-full gap-2 truncate">
                    <p
                      className={cx(
                        'truncate font-medium text-primary',
                        size === 'xs'
                          ? 'text-xs'
                          : size === 'sm'
                            ? 'text-sm'
                            : 'text-md',
                      )}
                    >
                      {state.selectedItem?.label}
                    </p>
                    {state.selectedItem?.supportingText && (
                      <p
                        className={cx(
                          // Gives way before the label when the trigger is narrow.
                          'min-w-0 shrink-[100] truncate text-tertiary',
                          size === 'xs'
                            ? 'text-xs'
                            : size === 'sm'
                              ? 'text-sm'
                              : 'text-md',
                        )}
                      >
                        {state.selectedItem?.supportingText}
                      </p>
                    )}
                  </section>
                ) : (
                  <p
                    className={cx(
                      'text-placeholder',
                      size === 'xs'
                        ? 'text-xs'
                        : size === 'sm'
                          ? 'text-sm'
                          : 'text-md',
                      isDisabled && 'text-disabled',
                    )}
                  >
                    {placeholder}
                  </p>
                )}

                {!trailingIcon && !showClearButton && (
                  <ChevronDown
                    aria-hidden="true"
                    className={cx(
                      'ml-auto shrink-0 text-fg-quaternary',
                      chevronSizeClass(size),
                    )}
                  />
                )}
              </>
            );
          }}
        </AriaSelectValue>
      </AriaButton>

      {!trailingIcon && showClearButton && (
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
          <ChevronDown
            aria-hidden="true"
            className={cx(
              'shrink-0 text-fg-quaternary',
              chevronSizeClass(size),
            )}
          />
        </div>
      )}

      {showClearButton && (
        <div className="absolute inset-y-0 right-8 flex items-center">
          <AriaButton
            slot={null}
            onPress={() => {
              if (onClear) {
                onClear();
              } else {
                selectState?.setValue(null);
              }
            }}
            className={({ isHovered, isFocusVisible }) =>
              cx(
                'flex cursor-pointer items-center rounded-sm p-0.5 text-fg-quaternary transition',
                isHovered && 'bg-primary_hover text-fg-quaternary_hover',
                isFocusVisible &&
                  'outline-2 outline-offset-2 outline-brand-solid',
              )
            }
            aria-label={t`Clear selection`}
          >
            <XClose className="size-4 stroke-[2.5px]" />
          </AriaButton>
        </div>
      )}

      {trailingIcon && (
        <div className="pointer-events-auto absolute inset-y-0 right-3 flex items-center">
          <ButtonContext.Provider value={emptyButtonContext}>
            {trailingIcon}
          </ButtonContext.Provider>
        </div>
      )}
    </div>
  );
};

export const SelectContext = createContext<{ size: 'xs' | 'sm' | 'md' }>({
  size: 'sm',
});

export type SelectSize = 'xs' | 'sm' | 'md';

const Select = <T extends string = string>({
  placeholder = 'Select',
  placeholderIcon,
  triggerIcon,
  trailingIcon,
  isClearable = false,
  onClear,
  size = 'sm',
  children,
  items,
  label,
  hint,
  tooltip,
  className,
  onSelectionChange,
  ...rest
}: SelectProps<T>) => {
  return (
    <SelectContext.Provider value={{ size }}>
      <AriaSelect
        {...rest}
        aria-label={label ? undefined : (rest['aria-label'] ?? placeholder)}
        onSelectionChange={
          onSelectionChange ? (key) => onSelectionChange(key as T) : undefined
        }
        className={(state) =>
          cx(
            'flex flex-col gap-1.5',
            typeof className === 'function' ? className(state) : className,
          )
        }
      >
        {(state) => (
          <>
            {label && (
              <Label
                isRequired={state.isRequired}
                isInvalid={state.isInvalid}
                tooltip={tooltip}
              >
                {label}
              </Label>
            )}

            <SelectValue
              {...state}
              {...{ size, placeholder }}
              placeholderIcon={placeholderIcon}
              triggerIcon={triggerIcon}
              trailingIcon={trailingIcon}
              isClearable={isClearable}
              onClear={onClear}
            />

            <Popover size={size} className={rest.popoverClassName}>
              <AriaListBox items={items} className="size-full outline-hidden">
                {children}
              </AriaListBox>
            </Popover>

            {hint && <HintText isInvalid={state.isInvalid}>{hint}</HintText>}
          </>
        )}
      </AriaSelect>
    </SelectContext.Provider>
  );
};

type SelectComponent = (<T extends string = string>(
  props: SelectProps<T>,
) => ReactNode) & {
  ComboBox: typeof ComboBox;
  Item: typeof SelectItem;
};

const _Select = Select as unknown as SelectComponent;
_Select.ComboBox = ComboBox;
_Select.Item = SelectItem;

export { _Select as Select };
